import {
  decodeRenderPackFlatBuffer,
  encodeRenderPackFlatBuffer,
  isRenderPackFlatBuffer,
} from "./render-pack-flatbuffer"

export const RENDER_PACK_SCHEMA_VERSION = "cogo-render-pack.v1" as const

export type RenderPackDiagnosticSeverity = "warning" | "error"

export interface RenderPackDiagnostic {
  code: string
  severity: RenderPackDiagnosticSeverity
  message: string
  nodeId?: string
  value?: string
}

export type RenderPackValue =
  | string
  | number
  | boolean
  | null
  | RenderPackValue[]
  | { [key: string]: RenderPackValue }

export interface RenderPackNode {
  id: string
  componentId: number
  parentIndex: number
  childIndices: number[]
  layoutOpStart: number
  layoutOpCount: number
  designOpStart: number
  designOpCount: number
  eventBindingStart: number
  eventBindingCount: number
  stateBindingStart: number
  stateBindingCount: number
  propOpStart: number
  propOpCount: number
}

export interface RenderPackLayoutOp {
  nodeIndex: number
  kind: "layout" | "responsive" | "slot"
  key: string
  value: RenderPackValue
}

export interface RenderPackDesignOp {
  nodeIndex: number
  kind: "design" | "token" | "authoringClass"
  key: string
  value: RenderPackValue
}

export interface RenderPackPropOp {
  nodeIndex: number
  kind: "prop" | "option"
  key: string
  value: RenderPackValue
}

export interface RenderPackBinding {
  nodeIndex: number
  kind: "event" | "action" | "state" | "datasource"
  key: string
  target: string
}

export interface RenderPack {
  schemaVersion: typeof RENDER_PACK_SCHEMA_VERSION
  componentIds: string[]
  actionIds: string[]
  tokenIds: string[]
  bindingIds: string[]
  nodes: RenderPackNode[]
  layoutOps: RenderPackLayoutOp[]
  propOps: RenderPackPropOp[]
  designOps: RenderPackDesignOp[]
  bindings: RenderPackBinding[]
  diagnostics: RenderPackDiagnostic[]
}

export interface CompileRenderPackOptions {
  componentUniverse?: readonly string[]
}

export interface CogoRenderPackInput {
  ui?: { root?: unknown }
  root?: unknown
  events?: unknown
  flow?: unknown
  data?: unknown
  state?: unknown
}

export interface MaterializedRenderPackTree {
  root?: Record<string, unknown>
  diagnostics: RenderPackDiagnostic[]
}

interface MutableRenderPack {
  componentIds: string[]
  actionIds: string[]
  tokenIds: string[]
  bindingIds: string[]
  nodes: RenderPackNode[]
  layoutOps: RenderPackLayoutOp[]
  propOps: RenderPackPropOp[]
  designOps: RenderPackDesignOp[]
  bindings: RenderPackBinding[]
  diagnostics: RenderPackDiagnostic[]
}

export function compileCogoRenderPack(
  input: CogoRenderPackInput,
  options: CompileRenderPackOptions = {},
): RenderPack {
  const root = readRecord(input.root) ?? readRecord(readRecord(input.ui)?.root)
  const knownComponents = new Set(options.componentUniverse ?? [])
  const pack: MutableRenderPack = {
    componentIds: [],
    actionIds: [],
    tokenIds: [],
    bindingIds: [],
    nodes: [],
    layoutOps: [],
    propOps: [],
    designOps: [],
    bindings: [],
    diagnostics: [],
  }

  if (!root) {
    pack.diagnostics.push({
      code: "render-pack-missing-root",
      severity: "error",
      message: "RenderPack input requires ui.root or root.",
    })
    return finalizeRenderPack(pack)
  }

  visitElement(root, -1, pack, knownComponents, readRecord(input.events))
  collectActionIds(input.flow, pack)
  collectDataBindings(input.data, pack)
  collectStateBindings(input.state, pack)

  return finalizeRenderPack(pack)
}

export function encodeRenderPack(pack: RenderPack): Uint8Array {
  return encodeRenderPackFlatBuffer(pack)
}

export function decodeRenderPack(bytes: Uint8Array): RenderPack {
  if (isRenderPackFlatBuffer(bytes)) return decodeRenderPackFlatBuffer(bytes)
  return normalizeDecodedRenderPack(JSON.parse(new TextDecoder().decode(bytes)) as Partial<RenderPack>)
}

export function materializeRenderPackTree(packOrBytes: RenderPack | Uint8Array): MaterializedRenderPackTree {
  const pack = packOrBytes instanceof Uint8Array ? decodeRenderPack(packOrBytes) : packOrBytes
  const rootNode = pack.nodes.find(node => node.parentIndex < 0) ?? pack.nodes[0]
  if (!rootNode) {
    return {
      diagnostics: [
        ...pack.diagnostics,
        {
          code: "render-pack-materialize-missing-root",
          severity: "error",
          message: "RenderPack has no nodes to materialize.",
        },
      ],
    }
  }

  return {
    root: materializeNode(pack, pack.nodes.indexOf(rootNode)),
    diagnostics: pack.diagnostics,
  }
}

function normalizeDecodedRenderPack(pack: Partial<RenderPack>): RenderPack {
  return {
    schemaVersion: pack.schemaVersion ?? RENDER_PACK_SCHEMA_VERSION,
    componentIds: pack.componentIds ?? [],
    actionIds: pack.actionIds ?? [],
    tokenIds: pack.tokenIds ?? [],
    bindingIds: pack.bindingIds ?? [],
    nodes: (pack.nodes ?? []).map(node => ({
      ...node,
      propOpStart: node.propOpStart ?? 0,
      propOpCount: node.propOpCount ?? 0,
    })),
    layoutOps: pack.layoutOps ?? [],
    propOps: pack.propOps ?? [],
    designOps: pack.designOps ?? [],
    bindings: pack.bindings ?? [],
    diagnostics: pack.diagnostics ?? [],
  }
}

function visitElement(
  element: Record<string, unknown>,
  parentIndex: number,
  pack: MutableRenderPack,
  knownComponents: Set<string>,
  events: Record<string, unknown> | undefined,
): number {
  const nodeIndex = pack.nodes.length
  const id = readString(element.id) ?? `node-${nodeIndex}`
  const componentType = readString(element.type) ?? "container"
  if (knownComponents.size > 0 && !knownComponents.has(componentType)) {
    pack.diagnostics.push({
      code: "render-pack-unknown-component",
      severity: "error",
      message: `Component "${componentType}" is not declared in the component universe.`,
      nodeId: id,
      value: componentType,
    })
  }

  const layoutStart = pack.layoutOps.length
  collectLayoutOps(element, nodeIndex, pack)
  const propStart = pack.propOps.length
  collectPropOps(element, nodeIndex, pack)
  const designStart = pack.designOps.length
  collectDesignOps(element, nodeIndex, pack)
  const bindingStart = pack.bindings.length
  collectEventBindings(id, element, events, nodeIndex, pack)
  const stateBindingStart = pack.bindings.length
  collectNodeStateBindings(element, nodeIndex, pack)

  const node: RenderPackNode = {
    id,
    componentId: intern(pack.componentIds, componentType),
    parentIndex,
    childIndices: [],
    layoutOpStart: layoutStart,
    layoutOpCount: pack.layoutOps.length - layoutStart,
    designOpStart: designStart,
    designOpCount: pack.designOps.length - designStart,
    eventBindingStart: bindingStart,
    eventBindingCount: stateBindingStart - bindingStart,
    stateBindingStart,
    stateBindingCount: pack.bindings.length - stateBindingStart,
    propOpStart: propStart,
    propOpCount: pack.propOps.length - propStart,
  }
  pack.nodes.push(node)

  const children = Array.isArray(element.children) ? element.children : []
  for (const child of children) {
    const childRecord = readRecord(child)
    if (!childRecord) continue
    node.childIndices.push(visitElement(childRecord, nodeIndex, pack, knownComponents, events))
  }

  return nodeIndex
}

function materializeNode(pack: RenderPack, nodeIndex: number): Record<string, unknown> {
  const node = pack.nodes[nodeIndex]
  const element: Record<string, unknown> = {
    id: node.id,
    type: pack.componentIds[node.componentId] ?? "container",
  }

  applyOps(element, "layout", pack.layoutOps.slice(node.layoutOpStart, node.layoutOpStart + node.layoutOpCount))
  applyPropOps(element, pack.propOps.slice(node.propOpStart, node.propOpStart + node.propOpCount))
  applyDesignOps(element, pack.designOps.slice(node.designOpStart, node.designOpStart + node.designOpCount))
  applyBindings(element, pack.bindings.slice(node.eventBindingStart, node.eventBindingStart + node.eventBindingCount))

  const children = node.childIndices.map(childIndex => materializeNode(pack, childIndex))
  if (children.length > 0) element.children = children
  return element
}

function applyOps(
  element: Record<string, unknown>,
  defaultKey: string,
  ops: Array<RenderPackLayoutOp | RenderPackDesignOp>,
): void {
  for (const op of ops) {
    if (op.kind === "responsive") {
      const layout = ensureRecord(element, "layout")
      const [index, key] = op.key.split(".", 2)
      const responsive = Array.isArray(layout.responsive) ? layout.responsive : []
      const numericIndex = Number(index)
      responsive[numericIndex] = {
        ...(readRecord(responsive[numericIndex]) ?? {}),
        [key ?? "value"]: op.value,
      }
      layout.responsive = responsive
      continue
    }
    const target = ensureRecord(element, op.kind === "slot" ? "slots" : defaultKey)
    target[op.key] = op.value
  }
}

function applyPropOps(element: Record<string, unknown>, ops: RenderPackPropOp[]): void {
  for (const op of ops) {
    if (op.kind === "option") {
      ensureRecord(element, "options")[op.key] = op.value
    } else {
      element[op.key] = op.value
    }
  }
}

function applyDesignOps(element: Record<string, unknown>, ops: RenderPackDesignOp[]): void {
  for (const op of ops) {
    if (op.kind === "authoringClass") {
      element[op.key] = op.value
      continue
    }
    const key = op.kind === "token" ? "tokens" : "design"
    ensureRecord(element, key)[op.key] = op.value
  }
}

function applyBindings(element: Record<string, unknown>, bindings: RenderPackBinding[]): void {
  for (const binding of bindings) {
    if (binding.kind !== "event") continue
    ensureRecord(element, "events")[binding.key] = { flow: binding.target }
  }
}

function collectLayoutOps(element: Record<string, unknown>, nodeIndex: number, pack: MutableRenderPack): void {
  pushRecordOps(pack.layoutOps, nodeIndex, "layout", readRecord(element.layout))
  pushRecordOps(pack.layoutOps, nodeIndex, "layout", readRecord(readRecord(element.options)?.layout))
  const responsive = readArray(element.responsive) ?? readArray(readRecord(element.layout)?.responsive)
  if (responsive) {
    responsive.forEach((entry, index) => {
      const record = readRecord(entry)
      if (!record) return
      for (const [key, value] of Object.entries(record)) {
        pushValueOp(pack.layoutOps, nodeIndex, "responsive", `${index}.${key}`, value)
      }
    })
  }
  const slots = readRecord(element.slots)
  if (slots) pushRecordOps(pack.layoutOps, nodeIndex, "slot", slots)
}

function collectDesignOps(element: Record<string, unknown>, nodeIndex: number, pack: MutableRenderPack): void {
  pushRecordOps(pack.designOps, nodeIndex, "design", readRecord(element.design))
  for (const key of ["variant", "density", "tone", "surface", "radius", "shadow", "typography"]) {
    pushValueOp(pack.designOps, nodeIndex, "design", key, element[key])
  }
  for (const key of ["designClass", "className"]) {
    const value = readString(element[key])
    if (!value) continue
    pack.designOps.push({ nodeIndex, kind: "authoringClass", key, value })
    for (const token of value.split(/\s+/).filter(Boolean)) {
      intern(pack.tokenIds, token)
    }
  }
  const tokens = readRecord(element.tokens) ?? readRecord(readRecord(element.options)?.designTokens)
  if (tokens) {
    for (const [key, value] of Object.entries(tokens)) {
      pushValueOp(pack.designOps, nodeIndex, "token", key, value)
      if (typeof value === "string") intern(pack.tokenIds, value)
    }
  }
}

function collectPropOps(element: Record<string, unknown>, nodeIndex: number, pack: MutableRenderPack): void {
  const excludedTopLevel = new Set([
    "children",
    "className",
    "design",
    "designClass",
    "events",
    "id",
    "layout",
    "responsive",
    "slots",
    "state",
    "style",
    "tokens",
    "type",
    "variables",
  ])
  for (const [key, value] of Object.entries(element)) {
    if (excludedTopLevel.has(key)) continue
    pushValueOp(pack.propOps, nodeIndex, "prop", key, value)
  }

  const options = readRecord(element.options)
  if (!options) return
  const excludedOptions = new Set(["designTokens", "layout", "style", "styling"])
  for (const [key, value] of Object.entries(options)) {
    if (excludedOptions.has(key)) continue
    pushValueOp(pack.propOps, nodeIndex, "option", key, value)
  }
}

function collectEventBindings(
  nodeId: string,
  element: Record<string, unknown>,
  events: Record<string, unknown> | undefined,
  nodeIndex: number,
  pack: MutableRenderPack,
): void {
  const localEvents = readRecord(element.events)
  if (localEvents) {
    for (const [eventName, binding] of Object.entries(localEvents)) {
      const target = readBindingTarget(binding)
      pushBinding(pack, nodeIndex, "event", eventName, target)
    }
  }
  const eventElements = readRecord(events?.elements)
  const external = readRecord(eventElements?.[nodeId])
  if (external) {
    for (const [eventName, binding] of Object.entries(external)) {
      const target = readBindingTarget(binding)
      pushBinding(pack, nodeIndex, "event", eventName, target)
    }
  }
}

function collectNodeStateBindings(element: Record<string, unknown>, nodeIndex: number, pack: MutableRenderPack): void {
  const variables = readRecord(element.variables) ?? readRecord(element.state)
  if (!variables) return
  for (const key of Object.keys(variables)) {
    pushBinding(pack, nodeIndex, "state", key, key)
  }
}

function collectActionIds(value: unknown, pack: MutableRenderPack): void {
  if (Array.isArray(value)) {
    value.forEach(item => collectActionIds(item, pack))
    return
  }
  const record = readRecord(value)
  if (!record) return
  const action = readString(record.action)
  if (action) intern(pack.actionIds, action)
  for (const child of Object.values(record)) collectActionIds(child, pack)
}

function collectDataBindings(value: unknown, pack: MutableRenderPack): void {
  const record = readRecord(value)
  if (!record) return
  for (const key of Object.keys(record)) {
    const bindingId = intern(pack.bindingIds, `data:${key}`)
    pack.bindings.push({ nodeIndex: -1, kind: "datasource", key, target: String(bindingId) })
  }
}

function collectStateBindings(value: unknown, pack: MutableRenderPack): void {
  const record = readRecord(value)
  if (!record) return
  for (const key of Object.keys(record)) {
    const bindingId = intern(pack.bindingIds, `state:${key}`)
    pack.bindings.push({ nodeIndex: -1, kind: "state", key, target: String(bindingId) })
  }
}

function pushRecordOps<T extends RenderPackLayoutOp | RenderPackDesignOp | RenderPackPropOp>(
  target: T[],
  nodeIndex: number,
  kind: T["kind"],
  record: Record<string, unknown> | undefined,
): void {
  if (!record) return
  for (const [key, value] of Object.entries(record)) {
    pushValueOp(target, nodeIndex, kind, key, value)
  }
}

function pushValueOp<T extends RenderPackLayoutOp | RenderPackDesignOp | RenderPackPropOp>(
  target: T[],
  nodeIndex: number,
  kind: T["kind"],
  key: string,
  value: unknown,
): void {
  const normalized = normalizeRenderPackValue(value)
  if (normalized === undefined) return
  target.push({ nodeIndex, kind, key, value: normalized } as T)
}

function pushBinding(
  pack: MutableRenderPack,
  nodeIndex: number,
  kind: RenderPackBinding["kind"],
  key: string,
  target: string,
): void {
  intern(pack.bindingIds, `${kind}:${key}:${target}`)
  pack.bindings.push({ nodeIndex, kind, key, target })
  if (kind === "event" && target) intern(pack.actionIds, target)
}

function readBindingTarget(value: unknown): string {
  if (typeof value === "string") return value
  const record = readRecord(value)
  return readString(record?.flow) ?? readString(record?.action) ?? readString(record?.target) ?? ""
}

function finalizeRenderPack(pack: MutableRenderPack): RenderPack {
  return {
    schemaVersion: RENDER_PACK_SCHEMA_VERSION,
    componentIds: pack.componentIds,
    actionIds: pack.actionIds,
    tokenIds: pack.tokenIds,
    bindingIds: pack.bindingIds,
    nodes: pack.nodes,
    layoutOps: pack.layoutOps,
    propOps: pack.propOps,
    designOps: pack.designOps,
    bindings: pack.bindings,
    diagnostics: pack.diagnostics,
  }
}

function intern(values: string[], value: string): number {
  const existing = values.indexOf(value)
  if (existing >= 0) return existing
  values.push(value)
  return values.length - 1
}

function readRecord(value: unknown): Record<string, unknown> | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) return undefined
  return value as Record<string, unknown>
}

function normalizeRenderPackValue(value: unknown): RenderPackValue | undefined {
  if (typeof value === "string" || typeof value === "boolean") return value
  if (typeof value === "number") return Number.isFinite(value) ? value : undefined
  if (value === null) return null
  if (Array.isArray(value) || readRecord(value)) {
    try {
      const encoded = JSON.stringify(value)
      if (encoded === undefined) return undefined
      return JSON.parse(encoded) as RenderPackValue
    } catch {
      return undefined
    }
  }
  return undefined
}

function ensureRecord(target: Record<string, unknown>, key: string): Record<string, unknown> {
  const existing = readRecord(target[key])
  if (existing) return existing
  const record: Record<string, unknown> = {}
  target[key] = record
  return record
}

function readArray(value: unknown): unknown[] | undefined {
  return Array.isArray(value) ? value : undefined
}

function readString(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined
}
